import React, { useState } from 'react';
import {
  GiftIcon,
  MagnifyingGlassIcon,
  UsersIcon,
  TrophyIcon,
} from '@heroicons/react/24/outline';
import {
  useAdminReferrals,
  useAdminReferralStats,
} from '../hooks/useAdminReferrals';
import { useDebounce } from '../hooks/useDebounce';
import { AdminReferralRow } from '../services/api';

const PAGE_SIZE = 20;

const fullName = (p?: { firstName?: string; lastName?: string }) =>
  [p?.firstName, p?.lastName].filter(Boolean).join(' ') || '—';

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString('de-DE', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });

const Referrals: React.FC = () => {
  const [search, setSearch] = useState('');
  const [skip, setSkip] = useState(0);
  // one backend query per settled term, not per keystroke
  const debouncedSearch = useDebounce(search, 300);

  const { data, isLoading, isError, refetch } = useAdminReferrals({
    limit: PAGE_SIZE,
    skip,
    search: debouncedSearch || undefined,
  });
  const { data: stats } = useAdminReferralStats();

  const rows = data?.referrals || [];
  const total = data?.total || 0;
  const topReferrer = stats?.topReferrers?.[0];

  const handleSearch = (value: string) => {
    setSearch(value);
    setSkip(0);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          Referrals
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Refer-a-Friend overview — who invited whom. The 1% bonus
          transactions are calculated by the Transaction Engine.
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3">
            <UsersIcon className="h-8 w-8 text-primary-600" />
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {stats?.totalReferred ?? '—'}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Referred customers
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3">
            <GiftIcon className="h-8 w-8 text-primary-600" />
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {stats?.uniqueReferrers ?? '—'}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Active referrers
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3">
            <TrophyIcon className="h-8 w-8 text-yellow-500" />
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {topReferrer
                  ? `${fullName(topReferrer)} (${topReferrer.referredCount})`
                  : '—'}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Top referrer
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <input
          type="text"
          value={search}
          onChange={(e) => handleSearch(e.target.value)}
          placeholder="Search by name or customer ID..."
          className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        />
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
            <tr>
              {['Referred customer', 'Country', 'Joined', 'Invited by', 'Code'].map(
                (h) => (
                  <th
                    key={h}
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                  >
                    {h}
                  </th>
                ),
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {isLoading ? (
              <tr>
                <td
                  colSpan={5}
                  className="px-4 py-8 text-center text-gray-500 dark:text-gray-400"
                >
                  Loading referrals...
                </td>
              </tr>
            ) : isError ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center">
                  <p className="text-red-600 dark:text-red-400 mb-2">
                    Failed to load referrals.
                  </p>
                  <button
                    type="button"
                    onClick={() => refetch()}
                    className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    Retry
                  </button>
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td
                  colSpan={5}
                  className="px-4 py-8 text-center text-gray-500 dark:text-gray-400"
                >
                  No referrals yet — they will appear here once customers
                  start inviting friends.
                </td>
              </tr>
            ) : (
              rows.map((row: AdminReferralRow) => (
                <tr
                  key={row.customerId}
                  className="hover:bg-gray-50 dark:hover:bg-gray-700/50"
                >
                  <td className="px-4 py-3">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {fullName(row)}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                      {row.customerId}
                    </p>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                    {row.country || '—'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                    {formatDate(row.createdAt)}
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {fullName(row.referrer)}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                      {row.referrer?.customerId || ''}
                    </p>
                  </td>
                  <td className="px-4 py-3">
                    {row.referrer?.referralCode ? (
                      <span className="inline-block px-2 py-0.5 text-xs font-semibold rounded bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300 tracking-wider">
                        {row.referrer.referralCode}
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* Pagination */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {total > 0
              ? `${skip + 1}–${Math.min(skip + PAGE_SIZE, total)} of ${total}`
              : '0 results'}
          </p>
          <div className="space-x-2">
            <button
              type="button"
              disabled={skip === 0 || isLoading}
              onClick={() => setSkip(Math.max(0, skip - PAGE_SIZE))}
              className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              type="button"
              disabled={skip + PAGE_SIZE >= total || isLoading}
              onClick={() => setSkip(skip + PAGE_SIZE)}
              className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Referrals;
